import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { MorphIcon } from './morph-icon.tsx'

/**
 * The tray (plan §1.7, OykaApp2/StalApp "TRAY"): everything you edit slides
 * over the page in one sheet — icon top-left (or ‹ on a sub-step), × top
 * right, a 22px title plus one sentence, the fields, and a footer with
 * Annuleren + one primary that says what happens. Height follows content;
 * 12px inset with a 30px radius on phones, a centred 480px dialog from 768px.
 *
 * Behaviour carried over from the portal Drawer (ADR-025 §6/§8): focus moves
 * into the dialog, Tab is trapped, Escape closes, focus returns to the
 * trigger, the page scroll is locked, the sheet lifts above the iOS keyboard
 * via `--oy-kb`, nothing accepts taps while the exit keyframe plays, and the
 * exit is one keyframe (the entry played in reverse).
 */
const EXIT_MS = 220

export type OyTrayTone = 'accent' | 'done' | 'warm' | 'quiet'

export type OyTrayPrimary = {
	label: string
	onClick?: () => void
	/** submit a form by id instead of calling onClick */
	form?: string
	tone?: OyTrayTone
	/** the "says what is missing" state: hint styling, aria-disabled, no submit */
	disabled?: boolean
}

function reducedMotion() {
	return (
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches
	)
}

function isDesktop() {
	return (
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(min-width: 768px)').matches
	)
}

export function OyTray({
	open,
	onClose,
	icon,
	iconBg,
	iconFg,
	title,
	intro,
	children,
	footer,
	back,
	note,
	className,
}: {
	open: boolean
	onClose: () => void
	/** the 44px round icon top-left (an emoji or a sprite Icon) */
	icon?: React.ReactNode
	iconBg?: string
	iconFg?: string
	title: string
	intro?: React.ReactNode
	children?: React.ReactNode
	footer?: { cancel?: string | null; primary: OyTrayPrimary }
	/** a sub-step: × becomes ‹ top-left and calls back */
	back?: { label: string; onClick: () => void }
	/** the small centred line under the footer */
	note?: React.ReactNode
	className?: string
}) {
	const [shown, setShown] = useState(open)
	const [closing, setClosing] = useState(false)
	const panelRef = useRef<HTMLDivElement>(null)
	const returnFocusRef = useRef<HTMLElement | null>(null)
	const titleId = useId()
	const introId = useId()

	useEffect(() => {
		if (open) {
			// where focus was before anything in the sheet mounts and takes it
			if (!shown && document.activeElement instanceof HTMLElement)
				returnFocusRef.current = document.activeElement
			setShown(true)
			setClosing(false)
			return
		}
		if (!shown) return
		if (reducedMotion()) {
			setShown(false)
			return
		}
		setClosing(true)
		const timer = window.setTimeout(() => {
			setShown(false)
			setClosing(false)
		}, EXIT_MS)
		return () => window.clearTimeout(timer)
	}, [open, shown])

	// Focus moves in once the panel is MOUNTED (shown lags open by a render),
	// onto a field marked data-autofocus or else the panel; where it came
	// from gets it back on close.
	useEffect(() => {
		if (!open || !shown) return
		const panel = panelRef.current
		if (!returnFocusRef.current && !panel?.contains(document.activeElement))
			returnFocusRef.current =
				document.activeElement instanceof HTMLElement
					? document.activeElement
					: null
		;(panel?.querySelector<HTMLElement>('[data-autofocus]') ?? panel)?.focus()
		return () => {
			const target = returnFocusRef.current
			if (!target) return
			if (
				document.activeElement instanceof HTMLElement &&
				document.activeElement !== target
			) {
				document.activeElement.blur()
			}
			target.focus()
			returnFocusRef.current = null
		}
	}, [open, shown])

	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault()
				onClose()
				return
			}
			// aria-modal promises the background is unreachable — keep Tab
			// cycling inside the panel.
			if (e.key !== 'Tab') return
			const panel = panelRef.current
			if (!panel) return
			const focusables = Array.from(
				panel.querySelectorAll<HTMLElement>(
					"button, input, select, textarea, a[href], [tabindex]:not([tabindex='-1'])",
				),
			).filter((el) => !el.hasAttribute('disabled'))
			if (focusables.length === 0) {
				e.preventDefault()
				panel.focus()
				return
			}
			const first = focusables[0]!
			const last = focusables[focusables.length - 1]!
			const active = document.activeElement
			if (e.shiftKey) {
				if (active === first || active === panel || !panel.contains(active)) {
					e.preventDefault()
					last.focus()
				}
			} else if (active === last || !panel.contains(active)) {
				e.preventDefault()
				first.focus()
			}
		}
		document.addEventListener('keydown', onKey)
		// <html> has overflow-x-hidden and is the real scroll container — lock
		// both, restore both.
		const prevBody = document.body.style.overflow
		const prevHtml = document.documentElement.style.overflow
		document.body.style.overflow = 'hidden'
		document.documentElement.style.overflow = 'hidden'
		return () => {
			document.removeEventListener('keydown', onKey)
			document.body.style.overflow = prevBody
			document.documentElement.style.overflow = prevHtml
		}
	}, [open, onClose])

	// iOS lays the keyboard over the layout viewport, so a bottom sheet loses
	// its lower half: lift it by the overlap (phones only).
	useEffect(() => {
		if (!open) return
		const vv = window.visualViewport
		if (!vv) return
		const root = document.documentElement
		const update = () => {
			if (isDesktop()) {
				root.style.removeProperty('--oy-kb')
				return
			}
			const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
			root.style.setProperty('--oy-kb', `${Math.round(kb)}px`)
		}
		update()
		vv.addEventListener('resize', update)
		vv.addEventListener('scroll', update)
		return () => {
			vv.removeEventListener('resize', update)
			vv.removeEventListener('scroll', update)
			root.style.removeProperty('--oy-kb')
		}
	}, [open])

	if (!shown) return null
	const primary = footer?.primary
	return (
		<>
			<button
				type="button"
				aria-label="Sluiten"
				tabIndex={-1}
				className={cn('oy-backdrop', closing && 'closing')}
				onClick={onClose}
			/>
			<div
				ref={panelRef}
				tabIndex={-1}
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
				aria-describedby={intro ? introId : undefined}
				className={cn('oy-tray', closing && 'closing', className)}
			>
				<div className="oy-tray-head">
					{back ? (
						<button
							type="button"
							className="oy-tray-ico"
							aria-label={back.label}
							onClick={back.onClick}
						>
							<MorphIcon state="back" />
						</button>
					) : (
						<span
							className="oy-tray-ico"
							aria-hidden="true"
							style={
								iconBg || iconFg
									? { background: iconBg, color: iconFg }
									: undefined
							}
						>
							{icon}
						</span>
					)}
					<button
						type="button"
						className="oy-tray-x"
						aria-label="Sluiten"
						onClick={onClose}
					>
						<MorphIcon state="cross" />
					</button>
				</div>
				<div className="oy-tray-body">
					<h2 id={titleId} className="oy-tray-title">
						{title}
					</h2>
					{intro ? (
						<p id={introId} className="oy-tray-intro">
							{intro}
						</p>
					) : null}
					{children}
					{footer ? (
						<div className="oy-tray-foot">
							{footer.cancel === null ? null : (
								<button
									type="button"
									className="oy-tray-cancel"
									onClick={onClose}
								>
									{footer.cancel ?? 'Annuleren'}
								</button>
							)}
							{primary ? (
								<button
									type={primary.form && !primary.disabled ? 'submit' : 'button'}
									form={primary.disabled ? undefined : primary.form}
									aria-disabled={primary.disabled ? true : undefined}
									className={cn(
										'oy-tray-primary',
										primary.tone && primary.tone !== 'accent'
											? `tone-${primary.tone}`
											: null,
									)}
									onClick={primary.onClick}
								>
									{primary.label}
								</button>
							) : null}
						</div>
					) : null}
					{note ? <p className="oy-tray-note">{note}</p> : null}
				</div>
			</div>
		</>
	)
}

/** A labelled field block inside the tray (18px above, 12/800 label). */
export function OyTrayField({
	label,
	hint,
	className,
	children,
}: {
	label: React.ReactNode
	/** the small centred line right under the field (StalApp isFoot) */
	hint?: React.ReactNode
	className?: string
	/** a node, or a function receiving the label's id for aria-labelledby */
	children: React.ReactNode | ((labelId: string) => React.ReactNode)
}) {
	const labelId = useId()
	return (
		<div className={cn('oy-tray-field', className)}>
			<div id={labelId} className="oy-label">
				{label}
			</div>
			{typeof children === 'function' ? children(labelId) : children}
			{hint ? <p className="oy-tray-foot-text">{hint}</p> : null}
		</div>
	)
}

/** Read-only a/b/c rows on the page ground (weeklink details, times). */
export function OyTrayRows({
	rows,
}: {
	rows: { a: React.ReactNode; b?: React.ReactNode; c?: React.ReactNode }[]
}) {
	return (
		<div className="oy-tray-rows">
			{rows.map((r, i) => (
				<div key={i} className="oy-tray-row">
					<span className="a">{r.a}</span>
					{r.b != null ? <span className="b">{r.b}</span> : null}
					{r.c != null ? <span className="c">{r.c}</span> : null}
				</div>
			))}
		</div>
	)
}

/** Menu rows inside a tray ("Iets doorgeven"): tile, label, sub, value, ›. */
export function OyTrayMenu({
	items,
}: {
	items: {
		icon: React.ReactNode
		label: React.ReactNode
		sub?: React.ReactNode
		value?: React.ReactNode
		tile?: string
		onClick: () => void
	}[]
}) {
	return (
		<div className="oy-tray-menu">
			{items.map((m, i) => (
				<button
					key={i}
					type="button"
					className="oy-tray-menurow"
					onClick={m.onClick}
				>
					<span
						className="oy-tile"
						aria-hidden="true"
						style={m.tile ? { background: m.tile } : undefined}
					>
						{m.icon}
					</span>
					<span className="oy-menurow-body">
						<span className="oy-menurow-title">{m.label}</span>
						{m.sub ? <span className="oy-menurow-sub">{m.sub}</span> : null}
					</span>
					{m.value ? <span className="oy-menurow-value">{m.value}</span> : null}
					<MorphIcon state="chevR" className="oy-menurow-chev" />
				</button>
			))}
		</div>
	)
}

/** A tinted note block (StalApp isNote). */
export function OyTrayNote({
	tone = 'accent',
	children,
}: {
	tone?: 'accent' | 'done' | 'warm' | 'muted'
	children: React.ReactNode
}) {
	return (
		<div className={cn('oy-tray-tint', tone !== 'accent' && `tone-${tone}`)}>
			{children}
		</div>
	)
}
